import React, { useMemo, useState } from 'react';
import {
  ArrowUpRight,
  Check,
  ChevronDown,
  Pencil,
  Plus,
  Radar,
  Route,
  Sparkles,
  Square,
  X,
} from 'lucide-react';
import { Button, CategoryPill, IconButton, Spinner } from '../../../components/ui';
import { NetworkAnalysis, ROLE_DESCRIPTION, sharedConnections } from '../graphAnalytics';
import { ConfidenceBadge, EvidenceList } from '../components/Confidence';
import { AiNotice, StreamProgress, canResearch, currentUserRole, isAdminRole } from '../components/BriefPanel';
import { userStorage } from '../../../utils/auth';
import type { ConnectionLead, NetworkLink, NetworkNode } from '../types';
import type { StreamState } from '../useIntelligenceStream';
import {
  KIND_LABEL,
  NetworkView,
  Vulnerabilities,
  careerOf,
  communityOf,
  formatSpan,
  groupTies,
  introductions,
  positionSummary,
  reachCounts,
  rankLabel,
  topPercent,
} from './networkModel';
import { EntityPicker } from './EntityPicker';
import { EntityAvatar, EntityRow, PercentileBar, RolePill, TieSwatch } from './ui';

export interface DossierProps {
  node: NetworkNode;
  view: NetworkView;
  /** links active in the current view */
  links: NetworkLink[];
  /**
   * Every tie the network holds, ended ones included. The canvas may be
   * showing current ties only; the dossier still knows what came before.
   */
  allLinks?: NetworkLink[];
  /** Turns the "include past ties" filter on, so the canvas draws them too */
  onShowPast?: () => void;
  analysis: NetworkAnalysis;
  names: Map<number, string>;
  vulns: Vulnerabilities;
  lineOf: (n: NetworkNode) => string;
  categoryPill: (node: NetworkNode) => { label: string; color?: string } | null;
  relationshipColor: (category?: string | null) => string | undefined;
  /* paths */
  pathTargetId: string | null;
  paths: string[][];
  activePath: number;
  onPathTarget: (id: string | null) => void;
  onActivePath: (index: number) => void;
  /* compare */
  compareId: string | null;
  onCompare: (id: string | null) => void;
  /* AI leads */
  leadsAvailable: boolean;
  leads: ConnectionLead[];
  freshLeadIds: Set<string>;
  discover: StreamState<{ leads: ConnectionLead[] }> & { subjectId: string | null };
  onDiscover: (focus: string) => void;
  onStopDiscover: () => void;
  onAccept: (lead: ConnectionLead) => Promise<void>;
  onDismiss: (lead: ConnectionLead) => Promise<void>;
  /** A counterpart the research found who is not on file yet */
  onAddToDirectory: (lead: ConnectionLead) => void;
  leadFocusId: string | null;
  onLeadFocus: (id: string | null) => void;
  /* navigation */
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onClose: () => void;
  onOpenProfile: () => void;
  onLink: () => void;
  onEditLink: (link: NetworkLink) => void;
  onFocusLens: () => void;
  onFocusCluster: (community: number) => void;
  className?: string;
  /** hide the header close button (phones use the sheet) */
  hideClose?: boolean;
}

const Section: React.FC<{ title: React.ReactNode; aside?: React.ReactNode; children: React.ReactNode; className?: string }> = ({
  title,
  aside,
  children,
  className = '',
}) => (
  <section className={`border-t border-neutral-200/70 px-4 py-3.5 ${className}`}>
    <div className="mb-2 flex min-h-[24px] items-center justify-between gap-2">
      <h3 className="text-[13px] text-ink">{title}</h3>
      {aside && <div className="flex items-center gap-1 text-[12px] text-neutral-400">{aside}</div>}
    </div>
    {children}
  </section>
);

/**
 * Ties that have ended, listed even when the canvas is drawing only what is
 * current: who somebody used to work with is part of who they are.
 */
const FormerTies: React.FC<{
  items: NetworkLink[];
  id: string;
  nodeOf: (id: string) => NetworkNode | undefined;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onShow?: () => void;
}> = ({ items, id, nodeOf, onSelect, onHover, onShow }) => {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, 3);
  return (
    <div>
      <p className="mb-0.5 flex items-center gap-2 text-[11.5px] text-neutral-500">
        <span className="h-px w-3 border-t border-dashed border-neutral-400" />
        Ended
        <span className="tabular-nums text-neutral-400">{items.length}</span>
        {onShow && (
          <button type="button" onClick={onShow} className="ml-auto text-[11.5px] text-neutral-400 hover:text-ink">
            Draw on the canvas
          </button>
        )}
      </p>
      <ul className="-mx-2">
        {shown.map((link) => {
          const otherId = link.source === id ? link.target : link.source;
          const other = nodeOf(otherId);
          const span = formatSpan(link.startDate, link.endDate, link.isCurrent);
          const sub = [KIND_LABEL[link.kind], link.label, span].filter(Boolean).join(' · ');
          return (
            <li key={link.id}>
              <EntityRow
                node={other}
                sub={sub}
                dim
                onClick={() => onSelect(otherId)}
                onHover={(h) => onHover(h ? otherId : null)}
              />
            </li>
          );
        })}
      </ul>
      {items.length > 3 && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-6 items-center gap-1 rounded-full px-1.5 text-[12px] text-neutral-500 hover:text-ink"
        >
          {open ? 'Show less' : `Show all ${items.length}`}
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
};

const MetricRow: React.FC<{ label: string; pct: number; hint: string }> = ({ label, pct, hint }) => (
  <div className="flex items-center gap-3 py-1" title={hint}>
    <span className="w-[68px] flex-shrink-0 text-[12px] text-neutral-500">{label}</span>
    <PercentileBar value={pct} className="flex-1" />
    <span className="w-[54px] flex-shrink-0 text-right text-[12px] tabular-nums text-ink">{rankLabel(pct)}</span>
  </div>
);

export const Dossier: React.FC<DossierProps> = (props) => {
  const { node, view, links, analysis, names, vulns, lineOf, onSelect, onHover } = props;
  const id = node.id;
  const m = analysis.metrics.get(id);
  const community = communityOf(analysis, id);
  const summary = useMemo(() => positionSummary(id, analysis, names, vulns.gatekeepers), [id, analysis, names, vulns]);
  const ties = useMemo(() => groupTies(id, links), [id, links]);
  const intros = useMemo(() => introductions(analysis, id, 4), [analysis, id]);
  const reach = useMemo(() => reachCounts(analysis, id), [analysis, id]);
  const [openKinds, setOpenKinds] = useState<Record<string, boolean>>({});
  const [showAllRoles, setShowAllRoles] = useState(false);
  const career = useMemo(() => careerOf(node), [node]);
  const held = useMemo(
    () => ({
      current: career.filter((r) => r.isCurrent).length,
      past: career.filter((r) => !r.isCurrent).length,
    }),
    [career],
  );
  // Ties the canvas is not drawing because they have ended: the dossier says
  // what it knows regardless of what the filters are showing
  const formerTies = useMemo(() => {
    if (!props.allLinks?.length) return [];
    const shown = new Set(links.map((l) => l.id));
    return props.allLinks.filter(
      (l) => !shown.has(l.id) && l.isCurrent === false && (l.source === id || l.target === id),
    );
  }, [props.allLinks, links, id]);
  const category = props.categoryPill(node);
  const n = (x: string) => view.nodesById.get(x);
  const tieTotal = ties.reduce((a, g) => a + g.items.length, 0);

  return (
    <div className={`min-w-0 ${props.className || ''}`}>
      {/* Header */}
      <header className="px-4 pb-3.5 pt-4">
        <div className="flex items-start gap-3">
          <EntityAvatar node={node} size="md" photo className="!h-11 !w-11" />
          <div className="min-w-0 flex-1 pt-0.5">
            <h2 className="truncate text-[16px] leading-tight tracking-[-0.015em] text-ink">{node.label}</h2>
            <p className="mt-0.5 truncate text-[12.5px] text-neutral-500">{lineOf(node)}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {m && <RolePill role={m.role} />}
              {category && node.type === 'stakeholder' && <CategoryPill label={category.label} color={category.color} />}
              {node.status && node.status !== 'approved' && <CategoryPill label={node.status} />}
            </div>
          </div>
          {!props.hideClose && (
            <IconButton label="Close dossier" size="sm" onClick={props.onClose} className="-mr-1.5 -mt-1">
              <X className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Button size="sm" variant="dark" onClick={props.onOpenProfile}>
            Profile
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </Button>
          <Button size="sm" variant="secondary" onClick={props.onLink}>
            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
            Link
          </Button>
          <Button size="sm" variant="ghost" onClick={props.onFocusLens} title="Radial view around this entity (2)">
            <Radar className="h-3.5 w-3.5" strokeWidth={1.75} />
            Focus
          </Button>
        </div>
      </header>

      {/* Positions held */}
      {career.length > 0 && (
        <Section
          title="Positions"
          aside={
            held.past > 0 ? (
              <span>
                {held.current} now · {held.past} before
              </span>
            ) : undefined
          }
        >
          <ul className="-mx-2">
            {(showAllRoles ? career : career.slice(0, 4)).map((role, index) => {
              const org = n(role.organizationId);
              const span = formatSpan(role.startDate, role.endDate, role.isCurrent);
              return (
                <li key={`${role.organizationId}:${role.title}:${index}`}>
                  <EntityRow
                    node={org}
                    label={role.title}
                    sub={[role.organizationName, role.department, span].filter(Boolean).join(' · ')}
                    dim={!role.isCurrent}
                    onClick={org ? () => onSelect(role.organizationId) : undefined}
                    onHover={(h) => org && onHover(h ? role.organizationId : null)}
                    trailing={
                      !role.isCurrent ? (
                        <span className="flex-shrink-0 text-[11px] text-neutral-400">former</span>
                      ) : undefined
                    }
                  />
                </li>
              );
            })}
          </ul>
          {career.length > 4 && (
            <button
              type="button"
              onClick={() => setShowAllRoles((v) => !v)}
              className="inline-flex h-6 items-center gap-1 rounded-full px-1.5 text-[12px] text-neutral-500 hover:text-ink"
            >
              {showAllRoles ? 'Show less' : `Show all ${career.length}`}
              <ChevronDown className={`h-3 w-3 transition-transform ${showAllRoles ? 'rotate-180' : ''}`} strokeWidth={1.75} />
            </button>
          )}
        </Section>
      )}

      {/* Network position */}
      <Section title="Network position">
        {m ? (
          <>
            <p className="text-[13px] leading-snug text-ink">{summary}</p>
            {m.role !== 'gatekeeper' && m.role !== 'isolate' && <p className="mt-1 text-[12px] leading-snug text-neutral-500">{ROLE_DESCRIPTION[m.role]}</p>}
            {m.degree > 0 && (
              <div className="mt-2.5">
                <MetricRow label="Influence" pct={m.pct.influence} hint="Tied to well-connected actors (PageRank)" />
                <MetricRow label="Brokerage" pct={m.pct.brokerage} hint="How often this actor sits between others" />
                <MetricRow label="Reach" pct={m.pct.reach} hint="Entities reachable through one intermediary" />
              </div>
            )}
            {community && (
              <button
                type="button"
                onClick={() => props.onFocusCluster(community.id)}
                className="mt-2 flex w-full items-center gap-2 rounded-xl bg-neutral-50 px-3 py-2 text-left text-[12.5px] transition-colors hover:bg-neutral-100"
              >
                <span className="text-neutral-500">Cluster</span>
                <span className="min-w-0 flex-1 truncate text-ink">{names.get(community.id)}</span>
                <span className="tabular-nums text-neutral-400">{community.members.length}</span>
              </button>
            )}
            {node.influence?.dominant && (
              <p className="mt-2 flex items-center gap-2 text-[12px] text-neutral-500">
                <span>Assessed influence</span>
                <span className="tabular-nums text-ink">{Math.round(node.influence.average)}</span>
                <CategoryPill label={`${node.influence.dominant.label} ${Math.round(node.influence.dominant.score)}`} color={node.influence.dominant.color} />
              </p>
            )}
          </>
        ) : (
          <p className="text-[12.5px] text-neutral-400">Hidden by the current filters</p>
        )}
      </Section>

      {/* Ties */}
      <Section
        title="Ties"
        aside={
          <span className="tabular-nums">
            {tieTotal}
            {formerTies.length > 0 && (
              <span className="text-neutral-400"> · {formerTies.length} ended</span>
            )}
          </span>
        }
      >
        {ties.length === 0 && formerTies.length === 0 ? (
          <p className="text-[12.5px] text-neutral-400">No ties in this view</p>
        ) : (
          <div className="space-y-2.5">
            {ties.map((group) => {
              const open = openKinds[group.kind];
              const items = open ? group.items : group.items.slice(0, 5);
              return (
                <div key={group.kind}>
                  <p className="mb-0.5 flex items-center gap-2 text-[11.5px] text-neutral-500">
                    <TieSwatch type={group.items[0].link.type} />
                    {KIND_LABEL[group.kind]}
                    <span className="tabular-nums text-neutral-400">{group.items.length}</span>
                  </p>
                  <ul className="-mx-2">
                    {items.map(({ link, otherId }) => {
                      const other = n(otherId);
                      const span = formatSpan(link.startDate, link.endDate, link.isCurrent);
                      const via =
                        link.kind === 'co_affiliation' && link.sharedOrganizationIds?.length
                          ? `via ${link.sharedOrganizationIds.map((o) => n(o)?.label).filter(Boolean).slice(0, 2).join(', ')}`
                          : '';
                      const sub = [via ? '' : link.label, via, span, link.isCurrent === false ? 'ended' : ''].filter(Boolean).join(' · ') || lineOf(other!);
                      const color = props.relationshipColor(link.category);
                      return (
                        <li key={link.id} className="group/tie relative">
                          <EntityRow
                            node={other}
                            sub={sub}
                            dim={link.isCurrent === false}
                            onClick={() => onSelect(otherId)}
                            onHover={(h) => onHover(h ? otherId : null)}
                            trailing={
                              link.kind === 'relationship' ? (
                                <span className="flex flex-shrink-0 items-center gap-1">
                                  {link.category && <CategoryPill label={link.category} color={color} className="max-w-[92px]" />}
                                  <span className="w-7" />
                                </span>
                              ) : undefined
                            }
                          />
                          {link.kind === 'relationship' && link.relationshipId && (
                            <IconButton
                              label="Edit relationship"
                              size="xs"
                              onClick={() => props.onEditLink(link)}
                              className="absolute right-1 top-1/2 -translate-y-1/2 opacity-50 group-hover/tie:opacity-100"
                            >
                              <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                            </IconButton>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {group.items.length > 5 && (
                    <button
                      type="button"
                      onClick={() => setOpenKinds((k) => ({ ...k, [group.kind]: !open }))}
                      className="inline-flex h-6 items-center gap-1 rounded-full px-1.5 text-[12px] text-neutral-500 hover:text-ink"
                    >
                      {open ? 'Show less' : `Show all ${group.items.length}`}
                      <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} />
                    </button>
                  )}
                </div>
              );
            })}
            {formerTies.length > 0 && <FormerTies items={formerTies} id={id} nodeOf={n} onSelect={onSelect} onHover={onHover} onShow={props.onShowPast} />}
          </div>
        )}
        {ties.length === 0 && formerTies.length > 0 && (
          <FormerTies items={formerTies} id={id} nodeOf={n} onSelect={onSelect} onHover={onHover} onShow={props.onShowPast} />
        )}
      </Section>

      {/* Reach & introductions */}
      {m && m.degree > 0 && (
        <Section
          title="Reach"
          aside={
            <span className="tabular-nums" title="Entities within two hops">
              {reach.twoHops} within 2 hops · {Math.round(reach.share * 100)}%
            </span>
          }
        >
          {intros.length === 0 ? (
            <p className="text-[12.5px] text-neutral-400">Everyone reachable is already a direct tie</p>
          ) : (
            <>
              <p className="mb-1 text-[11.5px] text-neutral-500">Most influential introductions</p>
              <ul className="-mx-2">
                {intros.map((intro) => (
                  <li key={intro.id}>
                    <EntityRow
                      node={n(intro.id)}
                      sub={
                        <>
                          via {n(intro.via[0])?.label}
                          {intro.via.length > 1 && <span className="text-neutral-400"> +{intro.via.length - 1}</span>}
                        </>
                      }
                      onClick={() => onSelect(intro.id)}
                      onHover={(h) => onHover(h ? intro.id : null)}
                      trailing={
                        (analysis.metrics.get(intro.id)?.pct.influence || 0) >= 50 ? (
                          <span className="flex-shrink-0 text-[11px] tabular-nums text-neutral-400">
                            {topPercent(analysis.metrics.get(intro.id)?.pct.influence || 0)}
                          </span>
                        ) : undefined
                      }
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </Section>
      )}

      {/* Paths to… */}
      <Section
        title="Paths to…"
        aside={
          props.pathTargetId && (
            <button type="button" onClick={() => props.onPathTarget(null)} className="rounded-full px-1.5 text-[12px] text-neutral-500 hover:text-ink">
              Clear
            </button>
          )
        }
      >
        {!props.pathTargetId ? (
          <EntityPicker nodes={view.nodes} exclude={[id]} lineOf={lineOf} onPick={props.onPathTarget} onHover={onHover} placeholder="Find paths to an entity" />
        ) : props.paths.length === 0 ? (
          <p className="text-[12.5px] text-neutral-500">
            No connection to <span className="text-ink">{n(props.pathTargetId)?.label}</span> in this view
          </p>
        ) : (
          <ol className="space-y-1.5">
            {props.paths.map((path, i) => (
              <li key={path.join('>')}>
                <button
                  type="button"
                  onClick={() => props.onActivePath(i)}
                  className={`w-full rounded-xl px-3 py-2 text-left transition-colors ${
                    i === props.activePath ? 'bg-primary-50/70 ring-1 ring-inset ring-primary-600/20' : 'bg-neutral-50 hover:bg-neutral-100'
                  }`}
                >
                  <span className="mb-1 flex items-center gap-1.5 text-[11.5px] text-neutral-500">
                    <Route className={`h-3.5 w-3.5 ${i === props.activePath ? 'text-primary-600' : 'text-neutral-400'}`} strokeWidth={1.75} />
                    {path.length - 1} {path.length === 2 ? 'hop' : 'hops'}
                    {i === 0 && <span className="text-neutral-400">· shortest</span>}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-[12.5px] leading-snug text-ink">
                    {path.map((pid, k) => (
                      <React.Fragment key={pid}>
                        {k > 0 && <span className="text-neutral-300">→</span>}
                        <span className={k === 0 || k === path.length - 1 ? '' : 'text-neutral-600'}>{n(pid)?.label}</span>
                      </React.Fragment>
                    ))}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </Section>

      {/* Compare */}
      <Section
        title="Compare"
        aside={
          props.compareId && (
            <button type="button" onClick={() => props.onCompare(null)} className="rounded-full px-1.5 text-[12px] text-neutral-500 hover:text-ink">
              Clear
            </button>
          )
        }
      >
        {!props.compareId ? (
          <EntityPicker nodes={view.nodes} exclude={[id]} lineOf={lineOf} onPick={props.onCompare} onHover={onHover} placeholder="Compare with…" />
        ) : (
          <CompareBlock {...props} otherId={props.compareId} />
        )}
      </Section>

      {/* AI leads */}
      <LeadsSection {...props} />
    </div>
  );
};

const CompareBlock: React.FC<DossierProps & { otherId: string }> = ({ node, otherId, analysis, view, onSelect, onHover, onPathTarget, pathTargetId }) => {
  const a = analysis.metrics.get(node.id);
  const b = analysis.metrics.get(otherId);
  const other = view.nodesById.get(otherId);
  const shared = useMemo(() => sharedConnections(analysis, node.id, otherId), [analysis, node.id, otherId]);
  const g = analysis.graph;
  const ia = g.index.get(node.id);
  const ib = g.index.get(otherId);
  const direct = ia !== undefined && ib !== undefined && g.adj[ia].has(ib);
  const sameCluster = a && b && a.community === b.community && analysis.communities.some((c) => c.id === a.community);
  const rows: Array<[string, (x: typeof a) => React.ReactNode]> = [
    ['Role', (x) => (x ? <RolePill role={x.role} /> : '—')],
    ['Influence', (x) => (x ? rankLabel(x.pct.influence) : '—')],
    ['Brokerage', (x) => (x ? rankLabel(x.pct.brokerage) : '—')],
    ['Reach', (x) => (x ? x.reach2 : '—')],
    ['Ties', (x) => (x ? x.degree : '—')],
  ];
  return (
    <div>
      <div className="overflow-hidden rounded-xl border border-neutral-200">
        <div className="grid grid-cols-[72px_1fr_1fr] items-center border-b border-neutral-200/70 bg-neutral-50/60 text-[12px]">
          <span />
          <span className="flex min-w-0 items-center gap-1.5 px-2 py-1.5">
            <EntityAvatar node={node} size="xs" className="!h-5 !w-5 !text-[9px]" />
            <span className="truncate text-ink">{node.label}</span>
          </span>
          <button type="button" onClick={() => onSelect(otherId)} className="flex min-w-0 items-center gap-1.5 px-2 py-1.5 text-left hover:underline">
            <EntityAvatar node={other} size="xs" className="!h-5 !w-5 !text-[9px]" />
            <span className="truncate text-ink">{other?.label}</span>
          </button>
        </div>
        {rows.map(([label, render]) => (
          <div key={label} className="grid grid-cols-[72px_1fr_1fr] items-center border-b border-neutral-200/70 text-[12.5px] tabular-nums last:border-b-0">
            <span className="px-2.5 py-1.5 text-[12px] text-neutral-500">{label}</span>
            <span className="px-2 py-1.5 text-ink">{render(a)}</span>
            <span className="px-2 py-1.5 text-ink">{render(b)}</span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[12.5px] leading-snug text-neutral-600">
        {direct ? 'Directly tied' : 'Not directly tied'}
        {sameCluster ? ' · same cluster' : ' · different clusters'}
        {!direct && pathTargetId !== otherId && (
          <>
            {' · '}
            <button type="button" onClick={() => onPathTarget(otherId)} className="text-ink underline decoration-neutral-300 underline-offset-2 hover:decoration-ink">
              show paths
            </button>
          </>
        )}
      </p>
      <p className="mb-1 mt-2.5 text-[11.5px] text-neutral-500">
        Shared connections <span className="tabular-nums text-neutral-400">{shared.length}</span>
      </p>
      {shared.length === 0 ? (
        <p className="text-[12.5px] text-neutral-400">None</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {shared.slice(0, 12).map((sid) => {
            const s = view.nodesById.get(sid);
            return (
              <button
                key={sid}
                type="button"
                onClick={() => onSelect(sid)}
                onMouseEnter={() => onHover(sid)}
                onMouseLeave={() => onHover(null)}
                className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-full bg-neutral-100 pl-0.5 pr-2.5 text-[12px] text-ink transition-colors hover:bg-neutral-200"
              >
                <EntityAvatar node={s} size="xs" className="!h-6 !w-6 !text-[9px]" />
                <span className="truncate">{s?.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* AI leads                                                            */
/* ------------------------------------------------------------------ */

const LeadsSection: React.FC<DossierProps> = ({
  node,
  leads,
  leadsAvailable,
  freshLeadIds,
  discover,
  onDiscover,
  onStopDiscover,
  onAccept,
  onDismiss,
  onAddToDirectory,
  leadFocusId,
  onLeadFocus,
  view,
  onSelect,
  onHover,
}) => {
  const [focus, setFocus] = useState('');
  const [showFocus, setShowFocus] = useState(false);
  const role = currentUserRole();
  const mine = discover.subjectId === node.id;
  const running = mine && discover.status === 'running';
  const failed = mine && (discover.status === 'error' || discover.status === 'unavailable');
  const relevant = leads.filter((l) => l.subjectId === node.id || l.targetId === node.id);
  const allowed = canResearch(role);

  return (
    <Section
      title={
        <span className="inline-flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary-600" strokeWidth={1.75} />
          AI leads
        </span>
      }
      aside={relevant.length > 0 && <span className="tabular-nums">{relevant.length} pending</span>}
      className="pb-5"
    >
      {!leadsAvailable ? (
        <AiNotice status="unavailable" error={null} />
      ) : !allowed ? (
        <p className="text-[12.5px] text-neutral-400">Research requires an admin or owner role</p>
      ) : running ? (
        <div className="rounded-xl bg-neutral-50 px-3 py-2.5">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[12.5px] text-ink">Researching {node.label}</span>
            <button type="button" onClick={onStopDiscover} className="inline-flex h-6 items-center gap-1 rounded-full px-2 text-[12px] text-neutral-500 hover:bg-white hover:text-ink">
              <Square className="h-2.5 w-2.5 fill-current" strokeWidth={1.75} />
              Stop
            </button>
          </div>
          <StreamProgress lines={discover.progress} running max={5} />
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => onDiscover(focus)} disabled={discover.status === 'running'}>
              {discover.status === 'running' ? <Spinner /> : <Sparkles className="h-3.5 w-3.5 text-primary-600" strokeWidth={1.75} />}
              Find connections
            </Button>
            {!showFocus && (
              <button type="button" onClick={() => setShowFocus(true)} className="h-8 rounded-full px-2 text-[12px] text-neutral-500 hover:text-ink">
                Add focus
              </button>
            )}
          </div>
          {discover.status === 'running' && !mine && <p className="mt-1.5 text-[12px] text-neutral-400">Another search is running</p>}
          {showFocus && (
            <input
              autoFocus
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onDiscover(focus)}
              placeholder="e.g. family ties, mining sector, board seats"
              aria-label="Research focus"
              className="mt-2 block h-9 w-full rounded-xl border-0 bg-white px-3 text-[16px] text-ink ring-1 ring-inset ring-neutral-200 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-600/30 sm:h-8 sm:text-[13px]"
            />
          )}
        </div>
      )}
      {failed && <AiNotice status={discover.status} error={discover.error} onRetry={() => onDiscover(focus)} className="mt-2" />}
      {mine && discover.status === 'done' && discover.result && discover.result.leads.length === 0 && (
        <p className="mt-2 text-[12.5px] text-neutral-500">No new connections found</p>
      )}

      {relevant.length > 0 && (
        <ul className="mt-3 space-y-2">
          {relevant.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              perspectiveId={node.id}
              fresh={freshLeadIds.has(lead.id)}
              view={view}
              role={role}
              focused={leadFocusId === lead.id}
              onFocus={onLeadFocus}
              onAccept={onAccept}
              onDismiss={onDismiss}
              onAdd={onAddToDirectory}
              onSelect={onSelect}
              onHover={onHover}
            />
          ))}
        </ul>
      )}
    </Section>
  );
};

const LeadCard: React.FC<{
  lead: ConnectionLead;
  perspectiveId: string;
  fresh: boolean;
  view: NetworkView;
  role: string;
  focused: boolean;
  onFocus: (id: string | null) => void;
  onAccept: (lead: ConnectionLead) => Promise<void>;
  onDismiss: (lead: ConnectionLead) => Promise<void>;
  /** Opens the form that puts this counterpart in the directory, filled in */
  onAdd: (lead: ConnectionLead) => void;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
}> = ({ lead, perspectiveId, fresh, view, role, focused, onFocus, onAccept, onDismiss, onAdd, onSelect }) => {
  const [sources, setSources] = useState(false);
  const [busy, setBusy] = useState<'accept' | 'dismiss' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const counterpartId = lead.subjectId === perspectiveId ? lead.targetId : lead.subjectId;
  const counterpartLabel = lead.subjectId === perspectiveId ? lead.targetLabel : lead.subjectLabel;
  const counterpart = counterpartId ? view.nodesById.get(counterpartId) : undefined;
  const userId = userStorage.get()?.id;
  const canReview = isAdminRole(role) || (role === 'top_owner' && lead.createdBy === userId);
  const inDirectory = !!lead.targetId;

  const run = async (kind: 'accept' | 'dismiss') => {
    setBusy(kind);
    setError(null);
    try {
      await (kind === 'accept' ? onAccept(lead) : onDismiss(lead));
    } catch (e: any) {
      const m = e?.response?.data?.message;
      setError((Array.isArray(m) ? m.join(', ') : m) || e?.message || 'Something went wrong');
      setBusy(null);
    }
  };

  return (
    <li
      onMouseEnter={() => onFocus(lead.id)}
      onMouseLeave={() => onFocus(null)}
      className={`rounded-xl border bg-white p-3 transition-colors ${fresh ? 'animate-fade-up motion-reduce:animate-none' : ''} ${
        focused ? 'border-primary-600/30' : 'border-neutral-200'
      }`}
    >
      <div className="flex items-start gap-2.5">
        {counterpart ? (
          <button type="button" onClick={() => onSelect(counterpart.id)} className="flex-shrink-0" aria-label={`Select ${counterpart.label}`}>
            <EntityAvatar node={counterpart} size="sm" />
          </button>
        ) : (
          <EntityAvatar node={{ id: 'x', type: lead.targetType, label: counterpartLabel, status: '' }} size="sm" className="opacity-60" />
        )}
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[13px] text-ink">{counterpartLabel}</span>
            {!inDirectory && (
              <button
                type="button"
                onClick={() => onAdd(lead)}
                title={`Not in the directory yet — add ${counterpartLabel} without leaving this page`}
                className="flex-shrink-0 rounded-full px-1.5 text-[10.5px] leading-4 text-neutral-500 ring-1 ring-inset ring-neutral-200 transition-colors hover:text-ink hover:ring-neutral-300"
              >
                Not in directory · Add
              </button>
            )}
          </p>
          <p className="truncate text-[12px] text-neutral-500">
            {lead.label}
            {lead.timeframe && <span className="text-neutral-400"> · {lead.timeframe}</span>}
          </p>
        </div>
        {fresh && <span className="mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-600" title="New" />}
      </div>
      <div className="mt-2">
        <ConfidenceBadge value={lead.confidence} />
      </div>
      <p className="mt-1.5 text-[12.5px] leading-snug text-neutral-700">{lead.summary}</p>
      <button
        type="button"
        aria-expanded={sources}
        onClick={() => setSources((s) => !s)}
        className="mt-1.5 inline-flex h-6 items-center gap-1 rounded-full text-[12px] text-neutral-500 hover:text-ink"
      >
        {lead.evidence.length} {lead.evidence.length === 1 ? 'source' : 'sources'}
        <ChevronDown className={`h-3 w-3 transition-transform ${sources ? 'rotate-180' : ''}`} strokeWidth={1.75} />
      </button>
      {sources && <EvidenceList evidence={lead.evidence} className="mt-1.5 animate-fade-up motion-reduce:animate-none" />}
      {error && <p className="mt-2 rounded-lg bg-primary-50 px-2.5 py-1.5 text-[12px] text-primary-700">{error}</p>}
      {canReview && (
        <div className="mt-2.5 flex items-center gap-1.5">
          <Button
            size="xs"
            variant="dark"
            loading={busy === 'accept'}
            disabled={!!busy || !inDirectory}
            onClick={() => run('accept')}
            title={inDirectory ? 'Create this relationship' : `Add ${counterpartLabel} to the directory first`}
          >
            {busy !== 'accept' && <Check className="h-3.5 w-3.5" strokeWidth={2} />}
            Accept
          </Button>
          <Button size="xs" variant="ghost" loading={busy === 'dismiss'} disabled={!!busy} onClick={() => run('dismiss')}>
            Dismiss
          </Button>
          <span className="ml-auto truncate text-[11px] text-neutral-400" title={`${lead.provider} · ${lead.model}`}>
            {lead.model}
          </span>
        </div>
      )}
    </li>
  );
};
